import { FC, useEffect, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Input } from "react-daisyui";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { useAuthContext } from "@/context/AuthContext.tsx";
import useUserUpdate from "@/api/users/useUserUpdate.ts";
import Routes from "@/router/Routes.ts";
import OnboardingStep from "$/_const/OnboardingStep.ts";
import { ArrowRightStartOnRectangleIcon } from "@heroicons/react/24/solid";
import { usePostHog } from "@posthog/react";

type YourNameFormData = {
    preferred_name: string;
};

const YourName: FC = () => {
    const posthog = usePostHog();
    const { t } = useTranslation();
    const { logout, user } = useAuthContext();
    const navigate = useNavigate();
    const { mutateAsync: updateUser, isPending, error } = useUserUpdate();

    const yourNameSchema = useMemo(
        () =>
            z.object({
                preferred_name: z
                    .string()
                    .trim()
                    .min(1, t("yourNamePage.validation.required"))
                    .max(150, t("yourNamePage.validation.maxLength")),
            }),
        [t],
    );

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<YourNameFormData>({
        resolver: zodResolver(yourNameSchema),
        defaultValues: {
            preferred_name: user?.preferred_name ?? "",
        },
    });

    useEffect(() => {
        reset({
            preferred_name: user?.preferred_name ?? "",
        });
    }, [user?.preferred_name, reset]);

    const onSubmit = (data: YourNameFormData) => {
        void (async () => {
            try {
                if (!user) return;

                await updateUser([
                    {
                        preferred_name: data.preferred_name.trim(),
                        onboarding_step: OnboardingStep.WELCOME,
                    },
                    {
                        id: user.id,
                    },
                ]);

                posthog?.capture("onboarding_step_completed", {
                    onboarding_step: "preferred_name",
                });
                navigate(Routes.ONBOARDING__WELCOME);
            } catch (error) {
                console.error(error);
                posthog?.capture("onboarding_step_failed", {
                    onboarding_step: "preferred_name",
                });
                posthog?.captureException(error, {
                    operation: "save_onboarding_preferred_name",
                    onboarding_step: "preferred_name",
                });
            }
        })();
    };

    return (
        <div className="fixed inset-0 overflow-y-auto bg-[var(--cream)] text-[var(--ink)]">
            <div className="flex min-h-full w-full items-center justify-center px-6 py-12 sm:py-16">
                <form
                    className="flex w-full max-w-[900px] flex-col items-center text-center"
                    onSubmit={handleSubmit(onSubmit)}
                    noValidate
                >
                    <h1 className="max-w-[700px] whitespace-pre-line font-feature text-[clamp(36px,4vw,44px)] font-semibold leading-[1.2] tracking-[-0.02em]">
                        <Trans
                            i18nKey="yourNamePage.title"
                            components={{
                                cody: (
                                    <span className="italic text-[var(--gold)]" />
                                ),
                            }}
                        />
                    </h1>

                    <p className="mt-8 max-w-[650px] font-sans text-base leading-[1.5] text-[var(--text-muted)] sm:text-lg">
                        {t("yourNamePage.intro")}
                    </p>

                    {error && (
                        <Alert
                            className="mt-7 w-full max-w-[464px] font-sans text-sm"
                            status="error"
                        >
                            {error.message}
                        </Alert>
                    )}

                    <div className="mt-10 flex w-full max-w-[434px] flex-col items-center">
                        <label htmlFor="preferred-name" className="sr-only">
                            {t("yourNamePage.inputLabel")}
                        </label>

                        <Controller
                            control={control}
                            name="preferred_name"
                            render={({ field }) => (
                                <Input
                                    {...field}
                                    id="preferred-name"
                                    type="text"
                                    maxLength={150}
                                    autoComplete="given-name"
                                    placeholder={t(
                                        "yourNamePage.inputPlaceholder",
                                    )}
                                    className={`!h-[50px] w-full !rounded-[9px] !bg-white px-4 font-sans text-base !text-[var(--ink)] placeholder:!font-light placeholder:!text-[var(--text-muted)] placeholder:!opacity-70 focus:!border-[var(--gold)] focus:!outline-none ${
                                        errors.preferred_name
                                            ? "!border-error"
                                            : "!border-[var(--gold-deep)]"
                                    }`}
                                    aria-invalid={Boolean(
                                        errors.preferred_name,
                                    )}
                                    aria-describedby={errors.preferred_name
                                        ? "preferred-name-error"
                                        : undefined}
                                />
                            )}
                        />

                        {errors.preferred_name && (
                            <p
                                id="preferred-name-error"
                                className="mt-2 w-full text-left font-sans text-sm text-error"
                                role="alert"
                            >
                                {errors.preferred_name.message}
                            </p>
                        )}

                        <Button
                            type="submit"
                            loading={isPending}
                            className="journi-primary-button mt-16 !min-h-0 w-full max-w-[362px] !rounded-[9px] !border-0 !bg-[var(--gold)] px-[34px] py-4 font-sans !text-base font-medium normal-case !text-[var(--ink)] shadow-[0_8px_24px_rgba(180,146,58,0.16)] transition-[transform,background-color,box-shadow] duration-500 ease-[cubic-bezier(.22,1,.36,1)] hover:!-translate-y-0.5 hover:!bg-[var(--gold-lt)] hover:shadow-[0_12px_28px_rgba(180,146,58,0.24)] active:!translate-y-0 active:!bg-[var(--gold-deep)]"
                        >
                            {t("yourNamePage.continue")}
                            <span aria-hidden="true">→</span>
                        </Button>
                        <Button
                            onClick={logout}
                            type="button"
                            color="ghost"
                            className="mt-4 !gap-4 text-error font-medium"
                            startIcon={
                                <ArrowRightStartOnRectangleIcon className="w-4" />
                            }
                        >
                            {t("button.logout")}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default YourName;
